import type { StoreValue } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import updateLocale from 'dayjs/plugin/updateLocale'
import utc from 'dayjs/plugin/utc'

export const TIMEZONE = 'Europe/London'

export const TIMEZONE_UTC = 'Etc/UTC'

export const ISO_STRING_FORMAT = 'YYYY-MM-DDTHH:mm:ss'

export const LONG_DATE_FORMAT = 'D MMM YYYY'

export const DATE_FORMAT = 'DD-MM-YYYY'

export const TIME_FORMAT = 'HH:mm'

export const YEAR_FIRST_DATE_FORMAT = 'YYYY-MM-DD'

export const DAY_FIRST_DATE_FORMAT = 'ddd DD-MM-YYYY'

export const TIME_FIRST_DATE_FORMAT = 'HH:mm DD-MM-YYYY'

export const TIME_LAST_DATE_FORMAT = 'DD-MM-YY HH:mm'

export const BACKEND_REQUEST_STRING_FORMAT = 'YYYY-MM-DD HH:mm'

export const HOURS_FORMAT = 'H[h]'

export const HOURS_AND_MINUTES_FORMAT = 'H[h] m[m]'

// Date not relevant here
export const START_TIME_PATTERN = '2024-01-01T08:00:00.000Z'

// Date not relevant here
export const END_TIME_PATTERN = '2024-01-01T09:00:00.000Z'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault(TIMEZONE_UTC)

export const getTodayDateFromTime = (value?: string | dayjs.Dayjs) => {
  const todayDate = dayjs().format(YEAR_FIRST_DATE_FORMAT)

  return `${todayDate}T${value}:00.000Z`
}

export const getTimeInZone = (
  value?: string | dayjs.Dayjs,
  isUserZone?: boolean
) => getDateTimeInZone(getTodayDateFromTime(value), isUserZone)

export const getDateTimeInZone = (
  dateTime: string | dayjs.Dayjs,
  isUserZone?: boolean
) => {
  if (isUserZone === true) {
    return dayjs(dateTime)
  }
  return dayjs(dateTime).tz(TIMEZONE_UTC)
}

export const getDateWithoutUTC = (
  date: Date | dayjs.Dayjs | string | undefined,
  format = YEAR_FIRST_DATE_FORMAT
) => dayjs(date).format(format)

export const getTimeWithoutUTC = (date: Date | dayjs.Dayjs | string) =>
  dayjs(date).format(TIME_FORMAT)

export const getMinutesToHours = (minutes = 0) =>
  Math.round((minutes / 60) * 2) / 2

export const formatMinutesAsHoursAndMinutes = (
  totalMinutes?: number | null
): string => {
  const total = Math.max(0, Math.floor(totalMinutes ?? 0))
  const hours = Math.floor(total / 60)
  const minutes = total % 60

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`
  }
  if (hours > 0) {
    return `${hours}h`
  }
  if (minutes > 0) {
    return `${minutes}m`
  }
  return '0h'
}

export const normalizeTimeDatePickerValues = (value: StoreValue) => {
  return {
    value: value ? [dayjs.utc(value[0]), dayjs.utc(value[1])] : undefined
  }
}

export const getTimeDiff = (
  start: string | Date | dayjs.Dayjs,
  end: string | Date | dayjs.Dayjs
) => {
  const diff = dayjs.duration(dayjs(end).diff(dayjs(start)))

  const timeFormat =
    diff.minutes() === 0 ? HOURS_FORMAT : HOURS_AND_MINUTES_FORMAT

  return diff.format(timeFormat)
}

export const getWeekBoundaries = (date: Date | string | dayjs.Dayjs) => {
  const day = dayjs.isDayjs(date) ? date : dayjs(date)
  const monday = day.isoWeekday(1).startOf('day')
  const sunday = day.isoWeekday(7).endOf('day')

  return [monday, sunday]
}

export const areSameWeek = (
  date1: Date | string | dayjs.Dayjs,
  date2: Date | string | dayjs.Dayjs
) => {
  const day1 = dayjs.isDayjs(date1) ? date1 : dayjs(date1)
  const day2 = dayjs.isDayjs(date2) ? date2 : dayjs(date2)

  return (
    day1.isoWeek() === day2.isoWeek() &&
    day1.isoWeekYear() === day2.isoWeekYear() &&
    day1.year() === day2.year()
  )
}

dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  weekStart: 1
})

export const disabledPastDate = (current: dayjs.Dayjs): boolean => {
  if (!current?.isValid()) {
    return false
  }
  return current.isBefore(dayjs(), 'day')
}

export const getDisabledPastTime = (selectedDate: dayjs.Dayjs | undefined) => {
  const isToday = selectedDate && dayjs(selectedDate).isSame(dayjs(), 'day')
  if (!isToday) {
    return {}
  }
  const now = dayjs()
  return {
    disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
    disabledMinutes: (hour: number) =>
      hour === now.hour()
        ? Array.from({ length: now.minute() + 1 }, (_, i) => i)
        : []
  }
}
